import React, { useState, useEffect, useRef } from 'react';
import {
  Edit, Delete, Settings, Group, ArrowForward, PersonAdd,
  Notifications, Image, Search, Refresh, Close, ExpandMore,
  ExpandLess, Star, Shield, Person, Key, BarChart, Visibility,
  Campaign, PhotoCamera, CheckCircle, Error as ErrorIcon,
  TrendingUp, People, AttachMoney, Add, Assessment, AccountBalanceWallet,
  ShowChart, ExpandCircleDown,
} from '@mui/icons-material';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Club {
  id: number;
  name: string;
  unique_club_id: string;
  owner_earnings_percentage: number;
  agent_earnings_percentage: number;
  member_earnings_percentage: number;
  profile_picture?: string;
  min_active_players_threshold?: number;
  active_player_wager_threshold?: number;
  clubrake?: number;
  created_at?: string;
}

interface Member {
  user_id: number;
  role: string;
  unique_agent_code: string | null;
  user_name: string;
  level: number;
  parent_name: string | null;
  agent_id: number | null;
  parent_user_id: number | null;
  full_hierarchy_path: string[];
}

interface Notification {
  id: number;
  title: string;
  body: string;
  type: string;
  created_at: string;
}

interface Banner {
  id: number;
  title: string;
  image_path: string;
  is_active: boolean;
  created_at: string;
}

interface ReportMember {
  user_id: number;
  user_name: string;
  role: string;
  unique_agent_code: string | null;
  agent_id: number | null;
  joined_at: string;
  last_active_at: string | null;
  total_wager: string;
  player_rake_percentage: string;
  estimated_earnings: string;
}

interface EarningsLog {
  id: number;
  distribution_date: string;
  total_club_earnings: string;
  owner_earnings: string;
  agent_earnings: string;
  active_player_count: number;
}

interface ReportSummary {
  totalMembers: number;
  activeMembers: number;
  totalWager: number;
  totalEstimatedEarnings: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const BASE_URL = 'https://api.growl.games/clubmembership';
const BANNER_URL = 'https://api.growl.games/clubbanner';
const NOTIF_URL = 'https://api.growl.games/clubnotification';

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const roleMeta: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  owner: { label: 'Owner', color: '#f59e0b', icon: <Star sx={{ fontSize: 14 }} /> },
  agent: { label: 'Agent', color: '#3b82f6', icon: <Shield sx={{ fontSize: 14 }} /> },
  member: { label: 'Member', color: '#10b981', icon: <Person sx={{ fontSize: 14 }} /> },
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const RoleBadge = ({ role }: { role: string }) => {
  const meta = roleMeta[role] ?? { label: role, color: '#6b7280', icon: null };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
      background: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}44`,
      textTransform: 'uppercase', letterSpacing: '0.5px',
    }}>
      {meta.icon}{meta.label}
    </span>
  );
};

const StatCard = ({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: string | number; color: string }) => (
  <div style={{
    background: '#1e293b', borderRadius: 12, padding: '16px 20px',
    border: `1px solid ${color}33`, flex: 1, minWidth: 120,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <span style={{ color, display: 'flex' }}>{icon}</span>
      <span style={{ color: '#94a3b8', fontSize: 12, fontWeight: 500 }}>{label}</span>
    </div>
    <div style={{ color, fontSize: 24, fontWeight: 700 }}>{value}</div>
  </div>
);

const SnackBar = ({ msg, type, onClose }: { msg: string; type: 'success' | 'error'; onClose: () => void }) => (
  <div style={{
    position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
    background: type === 'success' ? '#065f46' : '#7f1d1d',
    border: `1px solid ${type === 'success' ? '#10b981' : '#ef4444'}`,
    color: '#fff', padding: '12px 20px', borderRadius: 12,
    display: 'flex', alignItems: 'center', gap: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
    animation: 'slideUp 0.3s ease',
  }}>
    {type === 'success' ? <CheckCircle sx={{ fontSize: 18 }} /> : <ErrorIcon sx={{ fontSize: 18 }} />}
    <span style={{ fontSize: 14 }}>{msg}</span>
    <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', marginLeft: 8 }}>
      <Close sx={{ fontSize: 16 }} />
    </button>
  </div>
);

const Modal = ({ open, onClose, title, children, width = 520 }: {
  open: boolean; onClose: () => void; title: string; children: React.ReactNode; width?: number;
}) => {
  if (!open) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9000,
      background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: '#0f172a', border: '1px solid #1e293b', borderRadius: 16,
        width: '100%', maxWidth: width, maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
      }}>
        <div style={{
          padding: '20px 24px 16px', borderBottom: '1px solid #1e293b',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: '#0f172a', zIndex: 1,
        }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#f8fafc' }}>{title}</h3>
          <button onClick={onClose} style={{
            background: '#1e293b', border: 'none', color: '#94a3b8', borderRadius: 8,
            width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          }}>
            <Close sx={{ fontSize: 18 }} />
          </button>
        </div>
        <div style={{ padding: '20px 24px 24px' }}>{children}</div>
      </div>
    </div>
  );
};

const Input = ({ label, ...props }: { label: string } & React.InputHTMLAttributes<HTMLInputElement>) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#94a3b8' }}>{label}</label>
    <input {...props} style={{
      width: '100%', background: '#1e293b', border: '1px solid #334155',
      borderRadius: 8, padding: '10px 14px', color: '#f8fafc', fontSize: 14,
      outline: 'none', boxSizing: 'border-box',
      ...props.style,
    }} />
  </div>
);

const Textarea = ({ label, ...props }: { label: string } & React.TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#94a3b8' }}>{label}</label>
    <textarea {...props} style={{
      width: '100%', background: '#1e293b', border: '1px solid #334155',
      borderRadius: 8, padding: '10px 14px', color: '#f8fafc', fontSize: 14,
      outline: 'none', resize: 'vertical', minHeight: 80, boxSizing: 'border-box',
      ...props.style,
    }} />
  </div>
);

const Btn = ({ variant = 'primary', size = 'md', children, ...props }: {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  size?: 'sm' | 'md';
  children: React.ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) => {
  const colors = {
    primary: { bg: '#3b82f6', hover: '#2563eb', text: '#fff' },
    secondary: { bg: '#1e293b', hover: '#293548', text: '#94a3b8' },
    danger: { bg: '#dc262622', hover: '#dc262633', text: '#ef4444' },
    ghost: { bg: 'transparent', hover: '#1e293b', text: '#94a3b8' },
  };
  const c = colors[variant];
  const pad = size === 'sm' ? '6px 12px' : '10px 18px';
  const fs = size === 'sm' ? 12 : 14;
  return (
    <button {...props} style={{
      background: c.bg, color: c.text, border: `1px solid ${variant === 'secondary' ? '#334155' : 'transparent'}`,
      borderRadius: 8, padding: pad, fontSize: fs, fontWeight: 600,
      cursor: props.disabled ? 'not-allowed' : 'pointer', opacity: props.disabled ? 0.5 : 1,
      display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.15s',
      ...props.style,
    }}>
      {children}
    </button>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const ClubMembership: React.FC = () => {
  const [clubs, setClubs] = useState<Club[]>([]);
  const [filteredClubs, setFilteredClubs] = useState<Club[]>([]);
  const [selectedClub, setSelectedClub] = useState<Club | null>(null);
  const [hierarchy, setHierarchy] = useState<Member[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'members' | 'reports' | 'settings'>('overview');
  const [reportMembers, setReportMembers] = useState<ReportMember[]>([]);
  const [earningsLog, setEarningsLog] = useState<EarningsLog[]>([]);
  const [reportSummary, setReportSummary] = useState<ReportSummary | null>(null);
  const [expandedReportRows, setExpandedReportRows] = useState<Set<number>>(new Set());
  const [reportLoading, setReportLoading] = useState(false);
  const [clubSearch, setClubSearch] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [snack, setSnack] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [expandedMembers, setExpandedMembers] = useState<Set<number>>(new Set());

  // Modals
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [editClubOpen, setEditClubOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [bannerOpen, setBannerOpen] = useState(false);
  const [editBannerOpen, setEditBannerOpen] = useState<Banner | null>(null);

  // Form states
  const [memberForm, setMemberForm] = useState({ userId: '', joinMethod: 'clubId', agentCode: '', selectedAgentId: '' });
  const [clubForm, setClubForm] = useState({ name: '', profilePicture: null as File | null });
  const [configForm, setConfigForm] = useState({
    ownerEarningsPercentage: 0, agentEarningsPercentage: 0,
    memberEarningsPercentage: 0, activePlayerThreshold: 5, activePlayerWagerThreshold: 200,
  });
  const [notifForm, setNotifForm] = useState({ title: '', body: '', type: 'general' });
  const [bannerForm, setBannerForm] = useState({ title: '', image: null as File | null });
  const [submitting, setSubmitting] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const bannerFileRef = useRef<HTMLInputElement>(null);

  const showSnack = (msg: string, type: 'success' | 'error' = 'success') => {
    setSnack({ msg, type });
    setTimeout(() => setSnack(null), 4000);
  };

  // ─── Data Fetchers ────────────────────────────────────────────────────────

  const fetchClubs = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BASE_URL}/clubs/fetch`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setClubs(data.data ?? []);
    } catch {
      showSnack('Failed to fetch clubs', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchHierarchy = async (clubId: number) => {
    try {
      const res = await fetch(`${BASE_URL}/${clubId}/hierarchy`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error();
      const data = await res.json();
      const sorted = sortHierarchy(data.hierarchy ?? []);
      setHierarchy(sorted);
    } catch {
      showSnack('Failed to fetch hierarchy', 'error');
    }
  };

  const fetchNotifications = async (clubId: number) => {
    try {
      const res = await fetch(`${NOTIF_URL}/clubs/${clubId}/notifications`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setNotifications(data.data ?? []);
    } catch {
      showSnack('Failed to fetch notifications', 'error');
    }
  };

  const fetchBanners = async (clubId: number) => {
    try {
      const res = await fetch(`${BANNER_URL}/clubs/${clubId}/banners`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setBanners(data.data ?? []);
    } catch {
      showSnack('Failed to fetch banners', 'error');
    }
  };

  const fetchReports = async (clubId: number) => {
    setReportLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/${clubId}/reports`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setReportMembers(data.members ?? []);
      setEarningsLog(data.earningsLog ?? []);
      setReportSummary(data.summary ?? null);
    } catch {
      showSnack('Failed to fetch reports', 'error');
    } finally {
      setReportLoading(false);
    }
  };

  const sortHierarchy = (members: Member[]): Member[] => {
    const priority: Record<string, number> = { owner: 1, agent: 2, member: 3 };
    return [...members].sort((a, b) => {
      if (a.level !== b.level) return a.level - b.level;
      const pa = priority[a.role] ?? 9, pb = priority[b.role] ?? 9;
      if (pa !== pb) return pa - pb;
      return (a.full_hierarchy_path ?? []).join('-').localeCompare((b.full_hierarchy_path ?? []).join('-'));
    });
  };

  // ─── Effects ──────────────────────────────────────────────────────────────

  useEffect(() => { fetchClubs(); }, []);

  useEffect(() => {
    const q = clubSearch.toLowerCase();
    setFilteredClubs(clubs.filter(c => c.name.toLowerCase().includes(q) || c.unique_club_id.toLowerCase().includes(q)));
  }, [clubs, clubSearch]);

  useEffect(() => {
    if (!selectedClub) return;
    fetchHierarchy(selectedClub.id);
    if (activeTab === 'notifications') fetchNotifications(selectedClub.id);
    if (activeTab === 'banners') fetchBanners(selectedClub.id);
    if (activeTab === 'reports') fetchReports(selectedClub.id);
  }, [selectedClub?.id, activeTab]);

  const selectClub = (club: Club) => {
    setSelectedClub(club);
    setActiveTab('overview');
    setHierarchy([]);
    setNotifications([]);
    setBanners([]);
    fetchHierarchy(club.id);
  };

  // ─── Operations ───────────────────────────────────────────────────────────

  const handleAddMember = async () => {
    if (!selectedClub) return;
    setSubmitting(true);
    try {
      const payload = memberForm.joinMethod === 'clubId'
        ? { userId: memberForm.userId, clubId: selectedClub.unique_club_id }
        : { userId: memberForm.userId, agentCode: memberForm.agentCode };
      const res = await fetch(`${BASE_URL}/join`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.message); }
      showSnack('Member added successfully');
      setAddMemberOpen(false);
      setMemberForm({ userId: '', joinMethod: 'clubId', agentCode: '', selectedAgentId: '' });
      await fetchHierarchy(selectedClub.id);
    } catch (e: any) {
      showSnack(e.message ?? 'Failed to add member', 'error');
    } finally { setSubmitting(false); }
  };

  const handleEditClub = async () => {
    if (!selectedClub) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('clubId', selectedClub.id.toString());
      fd.append('clubName', clubForm.name);
      if (clubForm.profilePicture) fd.append('profilePicture', clubForm.profilePicture);
      const res = await fetch(`${BASE_URL}/update`, {
        method: 'PUT', headers: getAuthHeaders(), body: fd,
      });
      if (!res.ok) throw new Error();
      showSnack('Club updated');
      setEditClubOpen(false);
      await fetchClubs();
      const fresh = await fetch(`${BASE_URL}/clubs/fetch`, { headers: getAuthHeaders() });
      const fd2 = await fresh.json();
      const updated = (fd2.data ?? []).find((c: Club) => c.id === selectedClub.id);
      if (updated) setSelectedClub(updated);
    } catch {
      showSnack('Failed to update club', 'error');
    } finally { setSubmitting(false); }
  };

  const handleUpdateConfig = async () => {
    if (!selectedClub) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${BASE_URL}/earnings-config`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ clubId: selectedClub.id, ...configForm }),
      });
      if (!res.ok) throw new Error();
      showSnack('Configuration saved');
      setConfigOpen(false);
      await fetchClubs();
    } catch {
      showSnack('Failed to save configuration', 'error');
    } finally { setSubmitting(false); }
  };

  const handleDeleteClub = async () => {
    if (!selectedClub) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${BASE_URL}/${selectedClub.id}/delete`, {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error();
      showSnack('Club deleted');
      setDeleteConfirmOpen(false);
      setSelectedClub(null);
      setHierarchy([]);
      await fetchClubs();
    } catch {
      showSnack('Failed to delete club', 'error');
    } finally { setSubmitting(false); }
  };

  const handleChangeRole = async (userId: number, newRole: string) => {
    if (!selectedClub) return;
    try {
      const res = await fetch(`${BASE_URL}/change-role`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, clubId: selectedClub.id, newRole }),
      });
      if (!res.ok) throw new Error();
      showSnack(`Role changed to ${newRole}`);
      await fetchHierarchy(selectedClub.id);
    } catch {
      showSnack('Failed to change role', 'error');
    }
  };

  const handleSendNotification = async () => {
    if (!selectedClub || !notifForm.title || !notifForm.body) return;
    setSubmitting(true);
    try {
      const userId = JSON.parse(atob(localStorage.getItem('token')?.split('.')[1] ?? 'e30='))?.id ?? 1;
      const res = await fetch(`${NOTIF_URL}/clubs/${selectedClub.id}/notifications`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...notifForm, userId }),
      });
      if (!res.ok) throw new Error();
      showSnack('Notification sent to all members');
      setNotifOpen(false);
      setNotifForm({ title: '', body: '', type: 'general' });
      await fetchNotifications(selectedClub.id);
    } catch {
      showSnack('Failed to send notification', 'error');
    } finally { setSubmitting(false); }
  };

  const handleCreateBanner = async () => {
    if (!selectedClub || !bannerForm.title || !bannerForm.image) return;
    setSubmitting(true);
    try {
      const userId = JSON.parse(atob(localStorage.getItem('token')?.split('.')[1] ?? 'e30='))?.id ?? 1;
      const fd = new FormData();
      fd.append('title', bannerForm.title);
      fd.append('image', bannerForm.image);
      fd.append('userId', userId.toString());
      const res = await fetch(`${BANNER_URL}/clubs/${selectedClub.id}/banners`, {
        method: 'POST', headers: getAuthHeaders(), body: fd,
      });
      if (!res.ok) throw new Error();
      showSnack('Banner created');
      setBannerOpen(false);
      setBannerForm({ title: '', image: null });
      await fetchBanners(selectedClub.id);
    } catch {
      showSnack('Failed to create banner', 'error');
    } finally { setSubmitting(false); }
  };

  const handleDeleteBanner = async (bannerId: number) => {
    if (!selectedClub) return;
    try {
      const res = await fetch(`${BANNER_URL}/clubs/${selectedClub.id}/banners/${bannerId}`, {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error();
      showSnack('Banner deleted');
      await fetchBanners(selectedClub.id);
    } catch {
      showSnack('Failed to delete banner', 'error');
    }
  };

  // ─── Derived ──────────────────────────────────────────────────────────────

  const filteredHierarchy = memberSearch
    ? hierarchy.filter(m => m.user_name?.toLowerCase().includes(memberSearch.toLowerCase()) || m.role.includes(memberSearch.toLowerCase()))
    : hierarchy;

  const ownerCount = hierarchy.filter(m => m.role === 'owner').length;
  const agentCount = hierarchy.filter(m => m.role === 'agent').length;
  const memberCount = hierarchy.filter(m => m.role === 'member').length;

  // ─── Render ───────────────────────────────────────────────────────────────

  const tabs = [
    { key: 'overview', label: 'Overview', icon: <BarChart sx={{ fontSize: 16 }} /> },
    { key: 'members', label: `Members${hierarchy.length ? ` (${hierarchy.length})` : ''}`, icon: <People sx={{ fontSize: 16 }} /> },
    { key: 'reports', label: 'Claims & Reports', icon: <Assessment sx={{ fontSize: 16 }} /> },
    { key: 'settings', label: 'Settings', icon: <Settings sx={{ fontSize: 16 }} /> },
  ] as const;

  return (
    <div style={{ marginLeft: "256px", padding: '24px', background: '#020b18', minHeight: '100vh', fontFamily: 'Inter, system-ui, sans-serif' }}
    >
      <style>{`
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes spin { to { transform: rotate(360deg); } }
        .cm-tab-btn:hover { background: #1e293b !important; }
        .cm-club-card:hover { border-color: #3b82f6 !important; background: #1e293b !important; }
        .cm-member-row:hover { background: #1e293b !important; }
        .cm-btn-hover:hover { opacity: 0.85; }
        input[type=number]::-webkit-outer-spin-button, input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: #0f172a; }
        ::-webkit-scrollbar-thumb { background: #334155; border-radius: 4px; }
      `}</style>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#f8fafc' }}>Club Membership</h1>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>Manage clubs, members, and earnings</p>
        </div>
        <Btn onClick={fetchClubs} variant="secondary" size="sm">
          <Refresh sx={{ fontSize: 16 }} /> Refresh
        </Btn>
      </div>

      {/* ── Layout ─────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 20, alignItems: 'start' }}>

        {/* ── Left: Club List ─────────────────────────── */}
        <div style={{ background: '#0f172a', borderRadius: 16, border: '1px solid #1e293b', overflow: 'hidden' }}>
          <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid #1e293b' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ fontWeight: 700, color: '#f8fafc', fontSize: 15 }}>
                Clubs <span style={{ color: '#64748b', fontWeight: 400, fontSize: 13 }}>({clubs.length})</span>
              </span>
            </div>
            <div style={{ position: 'relative' }}>
              <Search sx={{ fontSize: 16, position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#475569' }} />
              <input
                placeholder="Search clubs…"
                value={clubSearch}
                onChange={e => setClubSearch(e.target.value)}
                style={{
                  width: '100%', background: '#1e293b', border: '1px solid #334155',
                  borderRadius: 8, padding: '8px 12px 8px 32px', color: '#f8fafc', fontSize: 13,
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

          <div style={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#475569' }}>
                <div style={{ width: 24, height: 24, border: '2px solid #334155', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
                Loading clubs…
              </div>
            ) : filteredClubs.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#475569', fontSize: 14 }}>No clubs found</div>
            ) : filteredClubs.map(club => (
              <div
                key={club.id}
                className="cm-club-card"
                onClick={() => selectClub(club)}
                style={{
                  padding: '14px 16px', cursor: 'pointer', borderBottom: '1px solid #1e293b',
                  background: selectedClub?.id === club.id ? '#1e293b' : 'transparent',
                  borderLeft: selectedClub?.id === club.id ? '3px solid #3b82f6' : '3px solid transparent',
                  transition: 'all 0.15s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: '#f1f5f9', fontSize: 14, marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {club.name}
                    </div>
                    <div style={{ color: '#64748b', fontSize: 11, fontFamily: 'monospace' }}>ID: {club.unique_club_id}</div>
                    {club.clubrake && (
                      <div style={{ color: '#f59e0b', fontSize: 11, marginTop: 2 }}>Rake: {club.clubrake}%</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    <button
                      onClick={e => { e.stopPropagation(); setSelectedClub(club); setClubForm({ name: club.name, profilePicture: null }); setEditClubOpen(true); }}
                      style={{ background: '#1e40af22', border: '1px solid #1e40af44', color: '#3b82f6', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 11 }}
                    >
                      <Edit sx={{ fontSize: 13 }} />
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); setSelectedClub(club); setDeleteConfirmOpen(true); }}
                      style={{ background: '#dc262622', border: '1px solid #dc262644', color: '#ef4444', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 11 }}
                    >
                      <Delete sx={{ fontSize: 13 }} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Right: Club Detail ──────────────────────── */}
        <div style={{ background: '#0f172a', borderRadius: 16, border: '1px solid #1e293b', overflow: 'hidden', minHeight: 500 }}>
          {!selectedClub ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 400, color: '#475569', gap: 12 }}>
              <Group sx={{ fontSize: 56, color: '#334155' }} />
              <span style={{ fontSize: 15, color: '#64748b' }}>Select a club to view details</span>
              <span style={{ fontSize: 13, color: '#475569' }}>Choose from the list on the left</span>
            </div>
          ) : (
            <>
              {/* Club Header */}
              <div style={{ padding: '20px 24px 0', borderBottom: '1px solid #1e293b' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{
                      width: 52, height: 52, borderRadius: 12, background: 'linear-gradient(135deg, #1e40af, #3b82f6)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <Group sx={{ color: '#fff', fontSize: 26 }} />
                    </div>
                    <div>
                      <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#f8fafc' }}>{selectedClub.name}</h2>
                      <div style={{ color: '#64748b', fontSize: 12, fontFamily: 'monospace', marginTop: 2 }}>ID: {selectedClub.unique_club_id}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <Btn size="sm" variant="secondary" onClick={() => setAddMemberOpen(true)}>
                      <PersonAdd sx={{ fontSize: 15 }} /> Add Member
                    </Btn>
                    <Btn size="sm" onClick={() => {
                      setConfigForm({
                        ownerEarningsPercentage: selectedClub.owner_earnings_percentage,
                        agentEarningsPercentage: selectedClub.agent_earnings_percentage,
                        memberEarningsPercentage: selectedClub.member_earnings_percentage,
                        activePlayerThreshold: selectedClub.min_active_players_threshold ?? 5,
                        activePlayerWagerThreshold: selectedClub.active_player_wager_threshold ?? 200,
                      }); setConfigOpen(true);
                    }}>
                      <Settings sx={{ fontSize: 15 }} /> Config
                    </Btn>
                  </div>
                </div>

                {/* Tabs */}
                <div style={{ display: 'flex', gap: 0, overflowX: 'auto' }}>
                  {tabs.map(tab => (
                    <button
                      key={tab.key}
                      className="cm-tab-btn"
                      onClick={() => setActiveTab(tab.key as any)}
                      style={{
                        background: activeTab === tab.key ? '#1e293b' : 'transparent',
                        border: 'none', borderBottom: activeTab === tab.key ? '2px solid #3b82f6' : '2px solid transparent',
                        color: activeTab === tab.key ? '#f8fafc' : '#64748b',
                        padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                        fontSize: 13, fontWeight: activeTab === tab.key ? 600 : 400, whiteSpace: 'nowrap',
                        transition: 'all 0.15s',
                      }}
                    >
                      {tab.icon}{tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div style={{ padding: '20px 24px' }}>

                {/* ── OVERVIEW ─────────────────────────── */}
                {activeTab === 'overview' && (
                  <div>
                    {/* Stats */}
                    <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
                      <StatCard icon={<People sx={{ fontSize: 20 }} />} label="Total Members" value={hierarchy.length} color="#3b82f6" />
                      <StatCard icon={<Star sx={{ fontSize: 20 }} />} label="Agents" value={agentCount} color="#f59e0b" />
                      <StatCard icon={<Shield sx={{ fontSize: 20 }} />} label="Members" value={memberCount} color="#10b981" />
                      <StatCard icon={<AttachMoney sx={{ fontSize: 20 }} />} label="Club Rake" value={`${selectedClub.clubrake ?? 0}%`} color="#8b5cf6" />
                    </div>

                    {/* Earnings Config Display */}
                    <div style={{ background: '#1e293b', borderRadius: 12, padding: '18px 20px', marginBottom: 20 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#94a3b8', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <TrendingUp sx={{ fontSize: 16 }} /> Earnings Configuration
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                        {[
                          { label: 'Owner', value: selectedClub.owner_earnings_percentage, color: '#f59e0b' },
                          { label: 'Agent', value: selectedClub.agent_earnings_percentage, color: '#3b82f6' },
                          { label: 'Member', value: selectedClub.member_earnings_percentage, color: '#10b981' },
                        ].map(item => (
                          <div key={item.label} style={{ background: '#0f172a', borderRadius: 8, padding: '12px 14px' }}>
                            <div style={{ color: '#64748b', fontSize: 11, marginBottom: 4 }}>{item.label}</div>
                            <div style={{ color: item.color, fontSize: 22, fontWeight: 700 }}>{item.value}%</div>
                            <div style={{ height: 4, background: '#1e293b', borderRadius: 4, marginTop: 8 }}>
                              <div style={{ width: `${item.value}%`, height: '100%', background: item.color, borderRadius: 4 }} />
                            </div>
                          </div>
                        ))}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                        <div style={{ background: '#0f172a', borderRadius: 8, padding: '10px 14px' }}>
                          <div style={{ color: '#64748b', fontSize: 11 }}>Min Active Players</div>
                          <div style={{ color: '#f8fafc', fontSize: 16, fontWeight: 600, marginTop: 4 }}>{selectedClub.min_active_players_threshold ?? 5}</div>
                        </div>
                        <div style={{ background: '#0f172a', borderRadius: 8, padding: '10px 14px' }}>
                          <div style={{ color: '#64748b', fontSize: 11 }}>Wager Threshold</div>
                          <div style={{ color: '#f8fafc', fontSize: 16, fontWeight: 600, marginTop: 4 }}>${selectedClub.active_player_wager_threshold ?? 200}</div>
                        </div>
                      </div>
                    </div>

                    {/* Earnings Stats (Real Commission Earned via Deposits & Games) */}
                    <div style={{ background: '#1e293b', borderRadius: 12, padding: '18px 20px', marginBottom: 20 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#94a3b8', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <AttachMoney sx={{ fontSize: 16 }} /> Actual Earning Stats
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                        {[
                          { title: 'Owners', role: 'owner', color: '#f59e0b' },
                          { title: 'Agents', role: 'agent', color: '#3b82f6' },
                          { title: 'Members', role: 'member', color: '#10b981' },
                        ].map(col => {
                          const users = hierarchy.filter(m => m.role === col.role);
                          return (
                            <div key={col.title} style={{ background: '#0f172a', borderRadius: 8, padding: '12px 14px', maxHeight: '250px', overflowY: 'auto' }}>
                              <div style={{ color: col.color, fontSize: 13, fontWeight: 700, marginBottom: 12, borderBottom: `1px solid ${col.color}33`, paddingBottom: 6 }}>
                                {col.title} ({users.length})
                              </div>
                              {users.length === 0 ? (
                                <div style={{ color: '#475569', fontSize: 11, textAlign: 'center', padding: '10px 0' }}>None</div>
                              ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                  {users.map(u => (
                                    <div key={u.user_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#1e293b', padding: '6px 10px', borderRadius: 6 }}>
                                      <div style={{ minWidth: 0, paddingRight: 8 }}>
                                        <div style={{ color: '#f8fafc', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.user_name}</div>
                                        <div style={{ color: '#64748b', fontSize: 10 }}>ID: {u.user_id}</div>
                                      </div>
                                      <div style={{ color: '#10b981', fontSize: 13, fontWeight: 700 }}>
                                        ${Number((u as any).total_earned_commission || 0).toFixed(2)}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* ── MEMBERS ──────────────────────────── */}
                {activeTab === 'members' && (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                      <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
                        <Search sx={{ fontSize: 15, position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#475569' }} />
                        <input
                          placeholder="Search members…"
                          value={memberSearch}
                          onChange={e => setMemberSearch(e.target.value)}
                          style={{
                            width: '100%', background: '#1e293b', border: '1px solid #334155',
                            borderRadius: 8, padding: '8px 12px 8px 32px', color: '#f8fafc', fontSize: 13,
                            outline: 'none', boxSizing: 'border-box',
                          }}
                        />
                      </div>
                      <Btn size="sm" variant="secondary" onClick={() => fetchHierarchy(selectedClub.id)}>
                        <Refresh sx={{ fontSize: 14 }} /> Reload
                      </Btn>
                    </div>

                    <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                      {[{ label: 'All', count: hierarchy.length, color: '#64748b' },
                      { label: 'Owner', count: ownerCount, color: '#f59e0b' },
                      { label: 'Agent', count: agentCount, color: '#3b82f6' },
                      { label: 'Member', count: memberCount, color: '#10b981' }].map(chip => (
                        <span key={chip.label} style={{
                          padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                          background: `${chip.color}22`, color: chip.color, border: `1px solid ${chip.color}44`,
                        }}>
                          {chip.label} · {chip.count}
                        </span>
                      ))}
                    </div>

                    {filteredHierarchy.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: 40, color: '#475569', fontSize: 14 }}>No members found</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {filteredHierarchy.map(member => (
                          <div
                            key={member.user_id}
                            className="cm-member-row"
                            style={{
                              marginLeft: member.level * 20,
                              background: '#1e293b', borderRadius: 10, padding: '12px 14px',
                              border: '1px solid #334155', transition: 'all 0.15s',
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                              {member.level > 0 && (
                                <ArrowForward sx={{ fontSize: 14, color: '#3b82f6', flexShrink: 0 }} />
                              )}
                              <div style={{
                                width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                                background: member.role === 'owner' ? '#f59e0b22' : member.role === 'agent' ? '#3b82f622' : '#10b98122',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>
                                {member.role === 'owner' ? <Star sx={{ fontSize: 16, color: '#f59e0b' }} />
                                  : member.role === 'agent' ? <Shield sx={{ fontSize: 16, color: '#3b82f6' }} />
                                    : <Person sx={{ fontSize: 16, color: '#10b981' }} />}
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 600, color: '#f1f5f9', fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {member.user_name}
                                </div>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 2, flexWrap: 'wrap' }}>
                                  <RoleBadge role={member.role} />
                                  {member.unique_agent_code && (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#64748b', fontSize: 11 }}>
                                      <Key sx={{ fontSize: 11 }} /> {member.unique_agent_code}
                                    </span>
                                  )}
                                  {member.parent_name && (
                                    <span style={{ color: '#475569', fontSize: 11 }}>under {member.parent_name}</span>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ color: '#94a3b8', fontSize: 11 }}>Earned</div>
                                <div style={{ color: '#10b981', fontSize: 14, fontWeight: 700 }}>${Number((member as any).total_earned_commission || 0).toFixed(2)}</div>
                              </div>
                              {member.role !== 'owner' && (
                                <select
                                  value={member.role}
                                  onChange={e => handleChangeRole(member.user_id, e.target.value)}
                                  style={{
                                    background: '#0f172a', border: '1px solid #334155', color: '#94a3b8',
                                    borderRadius: 6, padding: '5px 8px', fontSize: 12, cursor: 'pointer',
                                    outline: 'none', flexShrink: 0,
                                  }}
                                >
                                  <option value="member">Member</option>
                                  <option value="agent">Agent</option>
                                </select>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ── NOTIFICATIONS ─────────────────────── */}
                {activeTab === 'notifications' && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                      <Btn size="sm" onClick={() => setNotifOpen(true)}>
                        <Campaign sx={{ fontSize: 15 }} /> Send Notification
                      </Btn>
                    </div>
                    {notifications.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: 40, color: '#475569', fontSize: 14 }}>
                        <Notifications sx={{ fontSize: 36, color: '#334155', display: 'block', margin: '0 auto 12px' }} />
                        No notifications sent yet
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {notifications.map(n => (
                          <div key={n.id} style={{ background: '#1e293b', borderRadius: 10, padding: '14px 16px', border: '1px solid #334155' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                              <div>
                                <div style={{ fontWeight: 600, color: '#f1f5f9', fontSize: 14 }}>{n.title}</div>
                                <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>{n.body}</div>
                              </div>
                              <span style={{
                                padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                                background: '#3b82f622', color: '#3b82f6', border: '1px solid #3b82f644', flexShrink: 0,
                              }}>{n.type}</span>
                            </div>
                            <div style={{ color: '#475569', fontSize: 11, marginTop: 8 }}>
                              {new Date(n.created_at).toLocaleString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ── BANNERS ───────────────────────────── */}
                {activeTab === 'banners' && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                      <Btn size="sm" onClick={() => setBannerOpen(true)}>
                        <Add sx={{ fontSize: 15 }} /> New Banner
                      </Btn>
                    </div>
                    {banners.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: 40, color: '#475569', fontSize: 14 }}>
                        <Image sx={{ fontSize: 36, color: '#334155', display: 'block', margin: '0 auto 12px' }} />
                        No banners created yet
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
                        {banners.map(banner => (
                          <div key={banner.id} style={{ background: '#1e293b', borderRadius: 12, border: '1px solid #334155', overflow: 'hidden' }}>
                            <div style={{ height: 120, background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {banner.image_path ? (
                                <img
                                  src={`https://api.growl.games${banner.image_url}`}
                                  alt={banner.title}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                />
                              ) : (
                                <Image sx={{ fontSize: 40, color: '#334155' }} />
                              )}
                            </div>
                            <div style={{ padding: '12px 14px' }}>
                              <div style={{ fontWeight: 600, color: '#f1f5f9', fontSize: 14, marginBottom: 4 }}>{banner.title}</div>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span style={{
                                  padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                                  background: banner.is_active ? '#10b98122' : '#64748b22',
                                  color: banner.is_active ? '#10b981' : '#64748b',
                                  border: `1px solid ${banner.is_active ? '#10b98144' : '#64748b44'}`,
                                }}>
                                  {banner.is_active ? 'Active' : 'Inactive'}
                                </span>
                                <button
                                  onClick={() => handleDeleteBanner(banner.id)}
                                  style={{ background: '#dc262622', border: '1px solid #dc262644', color: '#ef4444', borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }}
                                >
                                  <Delete sx={{ fontSize: 14 }} />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ── CLAIMS & REPORTS ─────────────────── */}
                {activeTab === 'reports' && (
                  <div>
                    {reportLoading ? (
                      <div style={{ textAlign: 'center', padding: 40, color: '#475569' }}>Loading reports…</div>
                    ) : (
                      <>
                        {/* Summary Cards */}
                        {reportSummary && (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
                            {[
                              { label: 'Total Members', value: reportSummary.totalMembers, icon: <People sx={{ fontSize: 18 }} />, color: '#3b82f6' },
                              { label: 'Active Wagerers', value: reportSummary.activeMembers, icon: <ShowChart sx={{ fontSize: 18 }} />, color: '#10b981' },
                              { label: 'Total Wager', value: `$${reportSummary.totalWager.toLocaleString()}`, icon: <AttachMoney sx={{ fontSize: 18 }} />, color: '#f59e0b' },
                              { label: 'Est. Earnings', value: `$${reportSummary.totalEstimatedEarnings.toLocaleString()}`, icon: <AccountBalanceWallet sx={{ fontSize: 18 }} />, color: '#8b5cf6' },
                            ].map(card => (
                              <div key={card.label} style={{ background: '#1e293b', borderRadius: 12, padding: '14px 16px', border: `1px solid ${card.color}33` }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: card.color, marginBottom: 8 }}>
                                  {card.icon}
                                  <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>{card.label}</span>
                                </div>
                                <div style={{ fontSize: 22, fontWeight: 800, color: '#f8fafc' }}>{card.value}</div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Per-Member Breakdown */}
                        <div style={{ background: '#1e293b', borderRadius: 12, overflow: 'hidden', marginBottom: 20 }}>
                          <div style={{ padding: '14px 18px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 6 }}>
                              <People sx={{ fontSize: 16 }} /> Member Wager & Earnings Breakdown
                            </div>
                            <button
                              onClick={() => fetchReports(selectedClub.id)}
                              style={{ background: 'transparent', border: '1px solid #334155', borderRadius: 6, padding: '5px 10px', color: '#94a3b8', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}
                            >
                              <Refresh sx={{ fontSize: 13 }} /> Refresh
                            </button>
                          </div>

                          {/* Group by agent */}
                          {(() => {
                            const owners = reportMembers.filter(m => m.role === 'owner');
                            const agents = reportMembers.filter(m => m.role === 'agent');
                            const standalone = reportMembers.filter(m => m.role === 'member' && !m.agent_id);

                            const renderMemberRow = (m: ReportMember, isChild = false) => (
                              <div
                                key={m.user_id}
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'minmax(0, 2fr) 80px 100px 100px 100px 100px',
                                  gap: 8,
                                  padding: isChild ? '10px 18px 10px 40px' : '12px 18px',
                                  borderBottom: '1px solid #1e293b',
                                  background: isChild ? '#0a1628' : '#0f172a',
                                  alignItems: 'center',
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                  {isChild && <ArrowForward sx={{ fontSize: 12, color: '#3b82f6', flexShrink: 0 }} />}
                                  <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                    <div style={{ fontWeight: 600, color: '#f1f5f9', fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.user_name}</div>
                                    <div style={{ color: '#475569', fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>ID: {m.user_id}{m.unique_agent_code ? ` · Code: ${m.unique_agent_code}` : ''}</div>
                                  </div>
                                </div>
                                <span style={{
                                  padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, textAlign: 'center',
                                  background: m.role === 'owner' ? '#f59e0b22' : m.role === 'agent' ? '#3b82f622' : '#10b98122',
                                  color: m.role === 'owner' ? '#f59e0b' : m.role === 'agent' ? '#3b82f6' : '#10b981',
                                  border: `1px solid ${m.role === 'owner' ? '#f59e0b44' : m.role === 'agent' ? '#3b82f644' : '#10b98144'}`,
                                }}>
                                  {m.role}
                                </span>
                                <div style={{ color: '#94a3b8', fontSize: 12, textAlign: 'right' }}>
                                  {parseFloat(m.player_rake_percentage).toFixed(1)}%
                                </div>
                                <div style={{ color: '#f8fafc', fontSize: 13, fontWeight: 600, textAlign: 'right' }}>
                                  ${parseFloat(m.total_wager).toLocaleString()}
                                </div>
                                <div style={{ color: '#10b981', fontSize: 13, fontWeight: 700, textAlign: 'right' }}>
                                  ${parseFloat(m.estimated_earnings).toFixed(2)}
                                </div>
                                <div style={{ color: '#475569', fontSize: 11, textAlign: 'right' }}>
                                  {m.last_active_at ? new Date(m.last_active_at).toLocaleDateString() : '—'}
                                </div>
                              </div>
                            );

                            return (
                              <>
                                {/* Table header */}
                                <div style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'minmax(0, 2fr) 80px 100px 100px 100px 100px',
                                  gap: 8, padding: '10px 18px',
                                  borderBottom: '1px solid #334155',
                                  background: '#1e293b',
                                }}>
                                  {['Member', 'Role', 'Rake %', 'Total Wager', 'Est. Earnings', 'Last Active'].map(h => (
                                    <div key={h} style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, textAlign: h === 'Member' ? 'left' : 'right' }}>{h}</div>
                                  ))}
                                </div>

                                {/* Owners */}
                                {owners.map(m => renderMemberRow(m))}

                                {/* Agents with their members nested */}
                                {agents.map(agent => {
                                  const agentMembers = reportMembers.filter(m => m.agent_id === agent.user_id);
                                  const isExpanded = expandedReportRows.has(agent.user_id);
                                  const agentTotal = agentMembers.reduce((s, m) => s + parseFloat(m.total_wager || '0'), 0);
                                  return (
                                    <div key={agent.user_id}>
                                      {/* Agent row with expand toggle */}
                                      <div
                                        style={{
                                          display: 'grid',
                                          gridTemplateColumns: 'minmax(0, 2fr) 80px 100px 100px 100px 100px',
                                          gap: 8, padding: '12px 18px',
                                          borderBottom: '1px solid #1e293b',
                                          background: '#0f172a',
                                          alignItems: 'center',
                                          cursor: agentMembers.length > 0 ? 'pointer' : 'default',
                                        }}
                                        onClick={() => {
                                          if (!agentMembers.length) return;
                                          setExpandedReportRows(prev => {
                                            const next = new Set(prev);
                                            next.has(agent.user_id) ? next.delete(agent.user_id) : next.add(agent.user_id);
                                            return next;
                                          });
                                        }}
                                      >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                          {agentMembers.length > 0 && (
                                            isExpanded
                                              ? <ExpandLess sx={{ fontSize: 16, color: '#3b82f6' }} />
                                              : <ExpandMore sx={{ fontSize: 16, color: '#3b82f6' }} />
                                          )}
                                          <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                            <div style={{ fontWeight: 700, color: '#f1f5f9', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                              {agent.user_name}
                                              {agentMembers.length > 0 && (
                                                <span style={{ background: '#3b82f622', color: '#3b82f6', border: '1px solid #3b82f644', borderRadius: 999, fontSize: 10, padding: '1px 6px', fontWeight: 600, flexShrink: 0 }}>
                                                  {agentMembers.length} member{agentMembers.length !== 1 ? 's' : ''}
                                                </span>
                                              )}
                                            </div>
                                            <div style={{ color: '#475569', fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>ID: {agent.user_id}{agent.unique_agent_code ? ` · Code: ${agent.unique_agent_code}` : ''}</div>
                                          </div>
                                        </div>
                                        <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, textAlign: 'center', background: '#3b82f622', color: '#3b82f6', border: '1px solid #3b82f644' }}>
                                          agent
                                        </span>
                                        <div style={{ color: '#94a3b8', fontSize: 12, textAlign: 'right' }}>
                                          {parseFloat(agent.player_rake_percentage).toFixed(1)}%
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                          <div style={{ color: '#f8fafc', fontSize: 13, fontWeight: 600 }}>${parseFloat(agent.total_wager).toLocaleString()}</div>
                                          {agentMembers.length > 0 && (
                                            <div style={{ color: '#475569', fontSize: 10 }}>+${agentTotal.toLocaleString()} under</div>
                                          )}
                                        </div>
                                        <div style={{ color: '#10b981', fontSize: 13, fontWeight: 700, textAlign: 'right' }}>
                                          ${parseFloat(agent.estimated_earnings).toFixed(2)}
                                        </div>
                                        <div style={{ color: '#475569', fontSize: 11, textAlign: 'right' }}>
                                          {agent.last_active_at ? new Date(agent.last_active_at).toLocaleDateString() : '—'}
                                        </div>
                                      </div>

                                      {/* Nested members under agent */}
                                      {isExpanded && agentMembers.map(m => renderMemberRow(m, true))}
                                    </div>
                                  );
                                })}

                                {/* Standalone members (no agent) */}
                                {standalone.map(m => renderMemberRow(m))}

                                {reportMembers.filter(m => m.role !== 'owner').length === 0 && (
                                  <div style={{ textAlign: 'center', padding: 32, color: '#475569', fontSize: 13 }}>No member data available yet</div>
                                )}
                              </>
                            );
                          })()}
                        </div>

                        {/* Earnings Distribution Log */}
                        <div style={{ background: '#1e293b', borderRadius: 12, overflow: 'hidden' }}>
                          <div style={{ padding: '14px 18px', borderBottom: '1px solid #334155' }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 6 }}>
                              <ShowChart sx={{ fontSize: 16 }} /> Earnings Distribution History
                            </div>
                          </div>
                          {earningsLog.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: 32, color: '#475569', fontSize: 13 }}>No distributions recorded yet</div>
                          ) : (
                            <>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 80px', gap: 8, padding: '10px 18px', borderBottom: '1px solid #334155' }}>
                                {['Date', 'Total Earnings', 'Owner Cut', 'Agent Cut', 'Active'].map(h => (
                                  <div key={h} style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</div>
                                ))}
                              </div>
                              {earningsLog.map(log => (
                                <div key={log.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 80px', gap: 8, padding: '12px 18px', borderBottom: '1px solid #0f172a', alignItems: 'center' }}>
                                  <div style={{ color: '#94a3b8', fontSize: 13 }}>{new Date(log.distribution_date).toLocaleDateString()}</div>
                                  <div style={{ color: '#f59e0b', fontWeight: 700, fontSize: 13 }}>${parseFloat(log.total_club_earnings).toLocaleString()}</div>
                                  <div style={{ color: '#10b981', fontSize: 13 }}>${parseFloat(log.owner_earnings).toFixed(2)}</div>
                                  <div style={{ color: '#3b82f6', fontSize: 13 }}>${parseFloat(log.agent_earnings).toFixed(2)}</div>
                                  <div style={{ color: '#64748b', fontSize: 13 }}>{log.active_player_count}</div>
                                </div>
                              ))}
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* ── SETTINGS ─────────────────────────── */}
                {activeTab === 'settings' && (
                  <div style={{ maxWidth: 560 }}>
                    <div style={{ background: '#1e293b', borderRadius: 12, padding: '18px 20px', marginBottom: 16 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#94a3b8', marginBottom: 16 }}>Club Information</div>
                      <Btn onClick={() => { setClubForm({ name: selectedClub.name, profilePicture: null }); setEditClubOpen(true); }}>
                        <Edit sx={{ fontSize: 15 }} /> Edit Club Name & Photo
                      </Btn>
                    </div>
                    <div style={{ background: '#1e293b', borderRadius: 12, padding: '18px 20px', marginBottom: 16 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#94a3b8', marginBottom: 16 }}>Earnings & Thresholds</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                        {[
                          { label: 'Owner %', value: selectedClub.owner_earnings_percentage },
                          { label: 'Agent %', value: selectedClub.agent_earnings_percentage },
                          { label: 'Member %', value: selectedClub.member_earnings_percentage },
                          { label: 'Min Active Players', value: selectedClub.min_active_players_threshold ?? 5 },
                          { label: 'Wager Threshold ($)', value: selectedClub.active_player_wager_threshold ?? 200 },
                        ].map(item => (
                          <div key={item.label} style={{ background: '#0f172a', borderRadius: 8, padding: '10px 14px' }}>
                            <div style={{ color: '#64748b', fontSize: 11 }}>{item.label}</div>
                            <div style={{ color: '#f8fafc', fontWeight: 600, marginTop: 2 }}>{item.value}</div>
                          </div>
                        ))}
                      </div>
                      <Btn onClick={() => {
                        setConfigForm({
                          ownerEarningsPercentage: selectedClub.owner_earnings_percentage,
                          agentEarningsPercentage: selectedClub.agent_earnings_percentage,
                          memberEarningsPercentage: selectedClub.member_earnings_percentage,
                          activePlayerThreshold: selectedClub.min_active_players_threshold ?? 5,
                          activePlayerWagerThreshold: selectedClub.active_player_wager_threshold ?? 200,
                        }); setConfigOpen(true);
                      }}>
                        <Settings sx={{ fontSize: 15 }} /> Edit Configuration
                      </Btn>
                    </div>
                    <div style={{ background: '#1e293b', borderRadius: 12, padding: '18px 20px' }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#ef4444', marginBottom: 12 }}>Danger Zone</div>
                      <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 14 }}>
                        Deleting a club is permanent and cannot be undone. All members and data will be removed.
                      </p>
                      <Btn variant="danger" onClick={() => setDeleteConfirmOpen(true)}>
                        <Delete sx={{ fontSize: 15 }} /> Delete Club
                      </Btn>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          MODALS
      ═══════════════════════════════════════════════════════════ */}

      {/* Add Member */}
      <Modal open={addMemberOpen} onClose={() => setAddMemberOpen(false)} title="Add Member">
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#94a3b8' }}>Join Method</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {[{ val: 'clubId', label: 'Club ID' }, { val: 'agentCode', label: 'Agent Code' }].map(opt => (
              <button
                key={opt.val}
                onClick={() => setMemberForm({ ...memberForm, joinMethod: opt.val, agentCode: '', selectedAgentId: '' })}
                style={{
                  flex: 1, padding: '9px 0', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  background: memberForm.joinMethod === opt.val ? '#3b82f6' : '#1e293b',
                  border: `1px solid ${memberForm.joinMethod === opt.val ? '#3b82f6' : '#334155'}`,
                  color: memberForm.joinMethod === opt.val ? '#fff' : '#94a3b8',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <Input label="User ID" type="number" value={memberForm.userId} onChange={e => setMemberForm({ ...memberForm, userId: e.target.value })} placeholder="Enter user ID" />
        {memberForm.joinMethod === 'agentCode' && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#94a3b8' }}>Select Agent</label>
            <select
              value={memberForm.selectedAgentId}
              onChange={e => {
                const agent = hierarchy.find(m => m.user_id.toString() === e.target.value);
                setMemberForm({ ...memberForm, selectedAgentId: e.target.value, agentCode: agent?.unique_agent_code ?? '' });
              }}
              style={{ width: '100%', background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '10px 14px', color: '#f8fafc', fontSize: 14, outline: 'none' }}
            >
              <option value="">— Select an agent —</option>
              {hierarchy.filter(m => m.role === 'agent').map(agent => (
                <option key={agent.user_id} value={agent.user_id}>{agent.user_name} · {agent.unique_agent_code}</option>
              ))}
            </select>
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <Btn variant="secondary" onClick={() => setAddMemberOpen(false)} style={{ flex: 1 }}>Cancel</Btn>
          <Btn onClick={handleAddMember} disabled={submitting || !memberForm.userId} style={{ flex: 1 }}>
            {submitting ? 'Adding…' : 'Add Member'}
          </Btn>
        </div>
      </Modal>

      {/* Edit Club */}
      <Modal open={editClubOpen} onClose={() => setEditClubOpen(false)} title="Edit Club">
        <Input label="Club Name" value={clubForm.name} onChange={e => setClubForm({ ...clubForm, name: e.target.value })} placeholder="Club name" />
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600, color: '#94a3b8' }}>Profile Picture</label>
          <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }}
            onChange={e => { const f = e.target.files?.[0]; if (f) setClubForm({ ...clubForm, profilePicture: f }); }} />
          <div
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: '2px dashed #334155', borderRadius: 10, padding: '24px', textAlign: 'center',
              cursor: 'pointer', color: '#64748b', fontSize: 13, transition: 'all 0.15s',
            }}
          >
            <PhotoCamera sx={{ fontSize: 28, color: '#475569', display: 'block', margin: '0 auto 8px' }} />
            {clubForm.profilePicture ? (
              <span style={{ color: '#10b981' }}>{clubForm.profilePicture.name}</span>
            ) : (
              'Click to upload new profile picture'
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Btn variant="secondary" onClick={() => setEditClubOpen(false)} style={{ flex: 1 }}>Cancel</Btn>
          <Btn onClick={handleEditClub} disabled={submitting || !clubForm.name} style={{ flex: 1 }}>
            {submitting ? 'Saving…' : 'Save Changes'}
          </Btn>
        </div>
      </Modal>

      {/* Config */}
      <Modal open={configOpen} onClose={() => setConfigOpen(false)} title="Club Configuration" width={560}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Input label="Owner Earnings %" type="number" min="0" max="100" value={configForm.ownerEarningsPercentage}
            onChange={e => setConfigForm({ ...configForm, ownerEarningsPercentage: +e.target.value })} />
          <Input label="Agent Earnings %" type="number" min="0" max="100" value={configForm.agentEarningsPercentage}
            onChange={e => setConfigForm({ ...configForm, agentEarningsPercentage: +e.target.value })} />
          <Input label="Member Earnings %" type="number" min="0" max="100" value={configForm.memberEarningsPercentage}
            onChange={e => setConfigForm({ ...configForm, memberEarningsPercentage: +e.target.value })} />
          <Input label="Min Active Players" type="number" min="0" value={configForm.activePlayerThreshold}
            onChange={e => setConfigForm({ ...configForm, activePlayerThreshold: +e.target.value })} />
          <div style={{ gridColumn: '1 / -1' }}>
            <Input label="Wager Threshold ($)" type="number" min="0" value={configForm.activePlayerWagerThreshold}
              onChange={e => setConfigForm({ ...configForm, activePlayerWagerThreshold: +e.target.value })} />
          </div>
        </div>
        <div style={{
          background: '#1e293b', borderRadius: 8, padding: '10px 14px', marginBottom: 16,
          fontSize: 13, color: '#94a3b8',
        }}>
          Total earnings: <strong style={{ color: configForm.ownerEarningsPercentage + configForm.agentEarningsPercentage + configForm.memberEarningsPercentage > 100 ? '#ef4444' : '#10b981' }}>
            {configForm.ownerEarningsPercentage + configForm.agentEarningsPercentage + configForm.memberEarningsPercentage}%
          </strong>
          {configForm.ownerEarningsPercentage + configForm.agentEarningsPercentage + configForm.memberEarningsPercentage > 100 && (
            <span style={{ color: '#ef4444', marginLeft: 8 }}>⚠ Exceeds 100%</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Btn variant="secondary" onClick={() => setConfigOpen(false)} style={{ flex: 1 }}>Cancel</Btn>
          <Btn onClick={handleUpdateConfig} disabled={submitting} style={{ flex: 1 }}>
            {submitting ? 'Saving…' : 'Save Configuration'}
          </Btn>
        </div>
      </Modal>

      {/* Delete Confirm */}
      <Modal open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} title="Delete Club" width={400}>
        <div style={{ textAlign: 'center', padding: '8px 0 16px' }}>
          <Delete sx={{ fontSize: 48, color: '#ef4444', marginBottom: 12 }} />
          <p style={{ color: '#f1f5f9', fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Delete "{selectedClub?.name}"?</p>
          <p style={{ color: '#94a3b8', fontSize: 14, marginBottom: 24 }}>
            This will permanently delete the club and remove all {hierarchy.length} members. This cannot be undone.
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn variant="secondary" onClick={() => setDeleteConfirmOpen(false)} style={{ flex: 1 }}>Cancel</Btn>
            <Btn variant="danger" onClick={handleDeleteClub} disabled={submitting} style={{ flex: 1, background: '#dc2626', color: '#fff' }}>
              {submitting ? 'Deleting…' : 'Delete Club'}
            </Btn>
          </div>
        </div>
      </Modal>

      {/* Send Notification */}
      <Modal open={notifOpen} onClose={() => setNotifOpen(false)} title="Send Notification">
        <Input label="Title" value={notifForm.title} onChange={e => setNotifForm({ ...notifForm, title: e.target.value })} placeholder="Notification title" />
        <Textarea label="Message" value={notifForm.body} onChange={e => setNotifForm({ ...notifForm, body: e.target.value })} placeholder="Write your message…" />
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#94a3b8' }}>Type</label>
          <select value={notifForm.type} onChange={e => setNotifForm({ ...notifForm, type: e.target.value })}
            style={{ width: '100%', background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '10px 14px', color: '#f8fafc', fontSize: 14, outline: 'none' }}>
            <option value="general">General</option>
            <option value="announcement">Announcement</option>
            <option value="alert">Alert</option>
            <option value="reward">Reward</option>
          </select>
        </div>
        <div style={{ background: '#1e293b', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#94a3b8' }}>
          This will be sent to all <strong style={{ color: '#f8fafc' }}>{memberCount + agentCount}</strong> non-owner members of {selectedClub?.name}.
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Btn variant="secondary" onClick={() => setNotifOpen(false)} style={{ flex: 1 }}>Cancel</Btn>
          <Btn onClick={handleSendNotification} disabled={submitting || !notifForm.title || !notifForm.body} style={{ flex: 1 }}>
            {submitting ? 'Sending…' : 'Send to All Members'}
          </Btn>
        </div>
      </Modal>

      {/* Create Banner */}
      <Modal open={bannerOpen} onClose={() => setBannerOpen(false)} title="Create Banner">
        <Input label="Banner Title" value={bannerForm.title} onChange={e => setBannerForm({ ...bannerForm, title: e.target.value })} placeholder="Banner title" />
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600, color: '#94a3b8' }}>Banner Image</label>
          <input ref={bannerFileRef} type="file" accept="image/*" style={{ display: 'none' }}
            onChange={e => { const f = e.target.files?.[0]; if (f) setBannerForm({ ...bannerForm, image: f }); }} />
          <div
            onClick={() => bannerFileRef.current?.click()}
            style={{
              border: '2px dashed #334155', borderRadius: 10, padding: '32px', textAlign: 'center',
              cursor: 'pointer', color: '#64748b', fontSize: 13,
            }}
          >
            <Image sx={{ fontSize: 32, color: '#475569', display: 'block', margin: '0 auto 8px' }} />
            {bannerForm.image ? (
              <span style={{ color: '#10b981' }}>{bannerForm.image.name}</span>
            ) : (
              'Click to upload banner image'
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Btn variant="secondary" onClick={() => setBannerOpen(false)} style={{ flex: 1 }}>Cancel</Btn>
          <Btn onClick={handleCreateBanner} disabled={submitting || !bannerForm.title || !bannerForm.image} style={{ flex: 1 }}>
            {submitting ? 'Creating…' : 'Create Banner'}
          </Btn>
        </div>
      </Modal>

      {/* Snackbar */}
      {snack && <SnackBar msg={snack.msg} type={snack.type} onClose={() => setSnack(null)} />}
    </div>
  );
};

export default ClubMembership;
